import { type ComponentType, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import type { ModuleManifest } from "@praesto/contracts";
import { useBodyScrollLock } from "@praesto/ui-kit";

/**
 * Full-screen overlay host for the AI assistant — opened by the global
 * {@link AssistantFab}. Renders the procedure module's assistant component
 * obtained from the manifest at runtime (NOT a static import of
 * @praesto/procedure — the host stays free of a module dependency, same as the
 * router does for module routes).
 *
 * Best-practice "full sheet": covers the whole viewport including the bottom
 * nav, locks body scroll, closes on Esc / the in-chat close button / route
 * change, and restores the exact previous screen on close (no navigation).
 */
type CitationLike = { sopId: string };
type AssistantProps = {
  onClose?: () => void;
  onCitationClick?: (c: CitationLike) => void;
};

export function AssistantOverlay({
  manifests,
  open,
  onClose,
}: {
  manifests: ReadonlyArray<ModuleManifest>;
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug?: string };

  useBodyScrollLock(open);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Any route change underneath closes the overlay (e.g. a citation jump).
  useEffect(() => {
    if (open) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (!open) return null;

  const procedure = manifests.find((m) => m.id === "procedure");
  const assistantSub = procedure?.subRoutes?.find((s) => s.path === "assistant");
  const Assistant = assistantSub?.component as ComponentType<AssistantProps> | undefined;
  if (!Assistant) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col bg-canvas pt-safe"
      role="dialog"
      aria-modal="true"
    >
      <div className="min-h-0 flex-1">
        <Assistant
          onClose={onClose}
          onCitationClick={(c) => {
            onClose();
            if (tenantSlug) {
              void navigate({ to: `/${tenantSlug}/procedure/sops/${c.sopId}` as never });
            }
          }}
        />
      </div>
    </div>
  );
}
